import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine, RiInformationLine, RiPulseLine } from '@remixicon/react';
import type { ConnectorConfigDto, ConnectorDto, ConnectorLogLevel } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { Button, Card, KeyValueList, Skeleton, type KeyValueItem } from '@/shared/ui';
import { formatRegistryDate, formatReadingTime } from '@/features/devices/format';
import type { ConnectorsContext } from '../backend';
import { useConnectorLog, useSourceSensors, useTestConnector } from '../api';
import type { ConnectorTab } from '../ConnectorDetailPage';
import type { ConnectorRights } from '../permissions';
import { ConnectorStatusBadge, RelativeTime, ScopeBadge, useRelativeTime } from './parts';
import { HelpCard, SourceCard } from './unified';
import { useLogMessage } from './LogTab';

/**
 * "Umumiy": status (real live-source card for the unified server), source details, how-it-works, sensors per node and
 * the latest log events.
 */
export function OverviewTab({ c, ctx, rights, onTab }: { c: ConnectorDto; ctx: ConnectorsContext; rights: ConnectorRights; onTab: (tab: ConnectorTab) => void }) {
  const { t } = useTranslation('connectors');
  const unified = c.type === 'atnt_unified';
  return (
    <>
      <div className="grid shrink-0 grid-cols-[1fr_1fr_30rem] gap-10">
        {unified ? <SourceCard tenant={ctx.tenant} /> : <StatusCard c={c} ctx={ctx} canTest={rights.operate} />}
        <SourceInfoCard c={c} />
        {unified ? (
          <HelpCard />
        ) : (
          <Card
            title={
              <span className="flex items-center gap-8">
                <RiInformationLine aria-hidden className="size-18 text-accent-2" />
                {t('help.title')}
              </span>
            }
          >
            <p className="text-xs leading-17 text-fg-secondary">{t(`types.${c.type}.help`)}</p>
          </Card>
        )}
      </div>
      <div className="grid min-h-0 shrink-0 grid-cols-2 gap-10">
        <NodesCard c={c} ctx={ctx} onOpen={() => onTab('sensors')} />
        <RecentLogCard c={c} ctx={ctx} onOpen={() => onTab('log')} />
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-24 items-center justify-between gap-12">
      <dt className="text-xs text-fg-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-13 font-medium text-fg">{children}</dd>
    </div>
  );
}

function intervalOf(cfg: ConnectorConfigDto) {
  return 'pollIntervalSec' in cfg ? cfg.pollIntervalSec : null;
}

/** Status of a (mock-backed) connector + "Tekshirish". */
function StatusCard({ c, ctx, canTest }: { c: ConnectorDto; ctx: ConnectorsContext; canTest: boolean }) {
  const { t } = useTranslation('connectors');
  const test = useTestConnector(ctx);
  const sec = intervalOf(c.config);
  return (
    <Card
      title={t('overview.status')}
      description={t('overview.statusDesc')}
      actions={
        canTest ? (
          <Button size="sm" variant="secondary" leftIcon={RiPulseLine} loading={test.isPending} disabled={!c.enabled} onClick={() => test.mutate({ id: c.id })}>
            {t('menu.test')}
          </Button>
        ) : null
      }
    >
      <dl className="flex flex-col gap-8">
        <Row label={t('overview.state')}>
          <ConnectorStatusBadge status={c.status} />
        </Row>
        <Row label={t('overview.lastSync')}>
          <span className="flex items-center gap-6">
            <RelativeTime ts={c.lastSyncAt} className="text-fg-secondary" />
            {c.lastSyncAt && <span className="tabular-nums">{formatReadingTime(c.lastSyncAt)}</span>}
          </span>
        </Row>
        <Row label={t('overview.interval')}>{sec == null ? t('overview.push') : sec >= 60 ? t('every', { value: t('minutes', { count: Math.round(sec / 60) }) }) : t('every', { value: t('seconds', { count: sec }) })}</Row>
        <Row label={t('overview.bound')}>{t('source.boundValue', { bound: c.sensorsBound, total: c.sensorsTotal })}</Row>
        <Row label={t('overview.enabled')}>{c.enabled ? t('overview.yes') : t('overview.no')}</Row>
      </dl>
      {c.lastError && (
        <p role="alert" className="rounded-8 bg-tone-red-10 px-10 py-6 text-xs break-words text-tone-red-lighter">
          {c.lastError}
        </p>
      )}
    </Card>
  );
}

/** Where the data comes from: type, scope, endpoint, credentials hint, site code, creator. */
function SourceInfoCard({ c }: { c: ConnectorDto }) {
  const { t, i18n } = useTranslation('connectors');
  const cfg = c.config;
  const items: KeyValueItem[] = [
    { label: t('overview.type'), value: t(`types.${c.type}.name`) },
    { label: t('list.scope'), value: <ScopeBadge scope={c.scope} /> },
  ];
  const secret = (label: string, hint: { set: boolean; hint: string | null }) => ({ label, value: hint.set ? <span className="font-mono text-13">{hint.hint}</span> : t('overview.notSet') });
  switch (cfg.type) {
    case 'atnt_unified':
      items.push({ label: t('form.baseUrl'), value: cfg.baseUrl, wide: true }, secret(t('form.apiKey'), cfg.apiKey), { label: t('form.siteCode'), value: cfg.siteCode });
      break;
    case 'rest':
      items.push(
        { label: t('form.baseUrl'), value: cfg.baseUrl, wide: true },
        { label: t('form.auth'), value: t(`form.authKinds.${cfg.auth.kind}`) },
        cfg.auth.kind === 'header' ? secret(cfg.auth.headerName, cfg.auth.value) : cfg.auth.kind === 'bearer' ? secret(t('form.token'), cfg.auth.token) : { label: t('form.token'), value: null },
      );
      break;
    case 'mqtt':
      items.push({ label: t('form.brokerUrl'), value: cfg.brokerUrl, wide: true }, { label: t('form.topic'), value: cfg.topic }, { label: t('form.payloadFormat'), value: cfg.payloadFormat.toUpperCase() });
      break;
    case 'opcua':
      items.push({ label: t('form.endpointUrl'), value: cfg.endpointUrl, wide: true }, { label: t('form.securityMode'), value: t(`form.security.${cfg.securityMode}`) }, { label: t('form.nodeIds'), value: cfg.nodeIds.length });
      break;
    case 'modbus_tcp':
      items.push({ label: t('form.host'), value: `${cfg.host}:${cfg.port}` }, { label: t('form.unitId'), value: cfg.unitId }, { label: t('form.registers'), value: cfg.registers.length });
      break;
    case 'webhook':
      items.push({ label: t('form.webhookUrl'), value: cfg.endpointUrl, wide: true }, secret(t('form.webhookSecret'), cfg.secret));
      break;
    case 'file_import':
      items.push({ label: t('form.fileFormat'), value: cfg.format.toUpperCase() }, { label: t('form.columns'), value: cfg.columns.length });
      break;
  }
  items.push({ label: t('overview.createdAt'), value: formatRegistryDate(c.createdAt, i18n.language) }, { label: t('overview.createdBy'), value: c.createdBy?.fullName ?? t('overview.system') });
  return (
    <Card title={t('overview.source')} description={c.description ?? t('overview.sourceDesc')}>
      <KeyValueList items={items} columns={2} className="[&_dd]:truncate [&_dd]:text-13" />
    </Card>
  );
}

/** Bound / discovered sensors per node. */
function NodesCard({ c, ctx, onOpen }: { c: ConnectorDto; ctx: ConnectorsContext; onOpen: () => void }) {
  const { t } = useTranslation('connectors');
  const sensors = useSourceSensors(ctx, c.id);
  const nodes = new Map<string, { total: number; bound: number }>();
  for (const s of sensors.data ?? []) {
    const n = nodes.get(s.node) ?? { total: 0, bound: 0 };
    n.total += 1;
    if (s.binding) n.bound += 1;
    nodes.set(s.node, n);
  }
  return (
    <Card
      title={t('overview.nodes')}
      description={t('overview.nodesDesc', { count: nodes.size })}
      actions={
        <Button size="sm" variant="ghost" rightIcon={RiArrowRightSLine} onClick={onOpen}>
          {t('overview.all')}
        </Button>
      }
    >
      {sensors.isLoading ? (
        <div className="flex flex-col gap-10">
          <Skeleton className="h-14" />
          <Skeleton className="h-14 w-4/5" />
          <Skeleton className="h-14 w-3/5" />
        </div>
      ) : nodes.size === 0 ? (
        <p className="text-xs text-fg-secondary">{t('sensors.emptyHint')}</p>
      ) : (
        <ul className="flex flex-col gap-8">
          {[...nodes].slice(0, 7).map(([node, n]) => (
            <li key={node} className="grid grid-cols-[minmax(0,1fr)_10rem_4.5rem] items-center gap-12 text-13">
              <span className="truncate font-medium text-fg">{node === 'hydro-indicators' ? t('sensors.hydroNode') : node}</span>
              <span aria-hidden className="h-4 overflow-hidden rounded-full bg-field">
                <span className="block h-full rounded-full bg-accent-2" style={{ width: `${n.total ? (n.bound / n.total) * 100 : 0}%` }} />
              </span>
              <span className="text-right text-fg-secondary tabular-nums">
                <span className="text-fg">{n.bound}</span> / {n.total}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const LEVEL_DOT: Record<ConnectorLogLevel, string> = { info: 'bg-tone-green', warning: 'bg-tone-lime', error: 'bg-tone-red' };

function RecentLogCard({ c, ctx, onOpen }: { c: ConnectorDto; ctx: ConnectorsContext; onOpen: () => void }) {
  const { t } = useTranslation('connectors');
  const log = useConnectorLog(ctx, c.id);
  const message = useLogMessage();
  const rel = useRelativeTime();
  return (
    <Card
      title={t('overview.recent')}
      description={t('overview.recentDesc')}
      actions={
        <Button size="sm" variant="ghost" rightIcon={RiArrowRightSLine} onClick={onOpen}>
          {t('overview.all')}
        </Button>
      }
    >
      {log.isLoading ? (
        <div className="flex flex-col gap-10">
          <Skeleton className="h-14" />
          <Skeleton className="h-14 w-4/5" />
          <Skeleton className="h-14 w-3/5" />
        </div>
      ) : (
        <ul className="flex flex-col gap-8">
          {(log.data ?? []).slice(0, 7).map((e) => (
            <li key={e.id} className="grid grid-cols-[0.5rem_minmax(0,1fr)_auto] items-center gap-10 text-13">
              <span aria-hidden className={cn('size-6 rounded-full', LEVEL_DOT[e.level])} />
              <span className={cn('truncate', e.level === 'error' ? 'text-tone-red-lighter' : 'text-fg')}>{message(e)}</span>
              <span className="text-xs text-fg-secondary tabular-nums" title={formatReadingTime(e.at)}>
                {rel(e.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
